import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, Combine, Loader2, X } from 'lucide-react';

import SearchInputField from '../../../../components/Forms/Input/SearchInputField';
import CompactSelectField from '../../../../components/Forms/Input/CompactSelectField';
import ActionButton from '../../../../components/Buttons/ActionButton';

import { RestaurantDiningRoomDTO } from '../../../../types/restaurant/RestaurantDiningRoomTypes';
import { RestaurantOrderSummaryDTO } from '../../../../types/restaurant/RestaurantOrderTypes';
import { formatOrderMoney } from '../orderPresentation';

interface Props {
  open: boolean;
  currentOrder: RestaurantOrderSummaryDTO;
  rooms: RestaurantDiningRoomDTO[];
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onLoadTargets: (filters: {
    diningRoomId?: number;
    search?: string;
  }) => Promise<RestaurantOrderSummaryDTO[]>;
  onConfirm: (
    targetOrderId: number,
    sourceOrderId: number
  ) => Promise<unknown> | void;
}

const orderLabel = (order: RestaurantOrderSummaryDTO) =>
  `${order.tableName || order.orderNumber}${
    order.tableCode ? ` · ${order.tableCode}` : ''
  }`;

interface PickerProps {
  title: string;
  hint: string;
  rooms: RestaurantDiningRoomDTO[];
  namePrefix: string;
  options: RestaurantOrderSummaryDTO[];
  loading: boolean;
  selectedId: number | null;
  roomId: number | 'ALL';
  search: string;
  onRoomChange: (value: number | 'ALL') => void;
  onSearchChange: (value: string) => void;
  onSelect: (id: number) => void;
}

const OrderPicker = ({
  title,
  hint,
  rooms,
  namePrefix,
  options,
  loading,
  selectedId,
  roomId,
  search,
  onRoomChange,
  onSearchChange,
  onSelect,
}: PickerProps) => {
  const roomOptions = useMemo(
    () => [
      { value: 'ALL', label: 'Todos los salones' },
      ...rooms.map((room) => ({ value: room.id, label: room.name })),
    ],
    [rooms]
  );

  return (
    <div className="rounded-xl border border-stroke p-3 dark:border-strokedark">
      <p className="text-sm font-semibold text-black dark:text-white">{title}</p>
      <p className="mb-2 text-xs text-bodydark2">{hint}</p>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <CompactSelectField
          label="Salón"
          name={`${namePrefix}Room`}
          value={roomId}
          withMargin={false}
          options={roomOptions}
          onChange={(event) =>
            onRoomChange(
              event.target.value === 'ALL' ? 'ALL' : Number(event.target.value)
            )
          }
        />
        <SearchInputField
          label="Buscar orden"
          name={`${namePrefix}Search`}
          value={search}
          placeholder="Mesa u orden..."
          withMargin={false}
          size="sm"
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>

      <div className="mt-2 max-h-[20vh] space-y-1.5 overflow-y-auto pr-1">
        {loading ? (
          <div className="flex justify-center py-5">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => onSelect(option.id)}
                className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition ${
                  selectedId === option.id
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-stroke hover:border-primary/40 hover:bg-primary/5 dark:border-strokedark'
                }`}
              >
                <span>
                  {orderLabel(option)}
                  {option.diningRoomName ? (
                    <span className="ml-1 text-xs text-bodydark2">
                      ({option.diningRoomName})
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-xs font-semibold text-bodydark2">
                  {formatOrderMoney(option.total ?? 0)}
                </span>
              </button>
            ))}
            {!options.length && (
              <p className="py-5 text-center text-sm text-bodydark2">
                Sin órdenes con esos filtros.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const OrderMergeModal = ({
  open,
  currentOrder,
  rooms,
  busy,
  onOpenChange,
  onLoadTargets,
  onConfirm,
}: Props) => {
  const [targets, setTargets] = useState<RestaurantOrderSummaryDTO[]>([]);

  const [topRoom, setTopRoom] = useState<number | 'ALL'>('ALL');
  const [topSearch, setTopSearch] = useState('');
  const [topDebounced, setTopDebounced] = useState('');
  const [topId, setTopId] = useState<number | null>(currentOrder.id);
  const [loadingTop, setLoadingTop] = useState(false);
  const [topExtra, setTopExtra] = useState<RestaurantOrderSummaryDTO[]>([]);

  const [bottomRoom, setBottomRoom] = useState<number | 'ALL'>('ALL');
  const [bottomSearch, setBottomSearch] = useState('');
  const [bottomDebounced, setBottomDebounced] = useState('');
  const [bottomId, setBottomId] = useState<number | null>(null);
  const [loadingBottom, setLoadingBottom] = useState(false);
  const [bottomExtra, setBottomExtra] = useState<RestaurantOrderSummaryDTO[]>([]);

  useEffect(() => {
    if (open) {
      setTopRoom('ALL');
      setTopSearch('');
      setTopDebounced('');
      setTopId(currentOrder.id);
      setBottomRoom('ALL');
      setBottomSearch('');
      setBottomDebounced('');
      setBottomId(null);
    }
  }, [open, currentOrder.id]);

  useEffect(() => {
    const handle = setTimeout(() => setTopDebounced(topSearch), 300);
    return () => clearTimeout(handle);
  }, [topSearch]);
  useEffect(() => {
    const handle = setTimeout(() => setBottomDebounced(bottomSearch), 300);
    return () => clearTimeout(handle);
  }, [bottomSearch]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingTop(true);
    onLoadTargets({
      diningRoomId: topRoom === 'ALL' ? undefined : topRoom,
      search: topDebounced.trim() || undefined,
    })
      .then((data) => {
        if (!cancelled) setTopExtra(data);
      })
      .finally(() => {
        if (!cancelled) setLoadingTop(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, topRoom, topDebounced, onLoadTargets]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingBottom(true);
    onLoadTargets({
      diningRoomId: bottomRoom === 'ALL' ? undefined : bottomRoom,
      search: bottomDebounced.trim() || undefined,
    })
      .then((data) => {
        if (!cancelled) setBottomExtra(data);
      })
      .finally(() => {
        if (!cancelled) setLoadingBottom(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, bottomRoom, bottomDebounced, onLoadTargets]);

  // Mantiene una lista global para poder mostrar totales de la selección.
  useEffect(() => {
    setTargets([currentOrder, ...topExtra, ...bottomExtra]);
  }, [currentOrder, topExtra, bottomExtra]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onOpenChange, open]);

  const topOptions = useMemo(() => {
    const map = new Map<number, RestaurantOrderSummaryDTO>();
    [currentOrder, ...topExtra].forEach((order) => map.set(order.id, order));
    if (bottomId != null) map.delete(bottomId);
    return Array.from(map.values());
  }, [currentOrder, topExtra, bottomId]);

  const bottomOptions = useMemo(() => {
    const map = new Map<number, RestaurantOrderSummaryDTO>();
    [currentOrder, ...bottomExtra].forEach((order) => map.set(order.id, order));
    if (topId != null) map.delete(topId);
    return Array.from(map.values());
  }, [currentOrder, bottomExtra, topId]);

  const topOrder = targets.find((order) => order.id === topId) ?? null;
  const bottomOrder = targets.find((order) => order.id === bottomId) ?? null;
  const combined =
    (Number(topOrder?.total ?? 0) || 0) +
    (Number(bottomOrder?.total ?? 0) || 0);
  const ready = topId != null && bottomId != null && topId !== bottomId;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[8999] flex items-center justify-center bg-black/60 px-3 py-6 backdrop-blur-[2px]"
      role="presentation"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-merge-title"
        className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark"
      >
        <header className="flex items-center justify-between border-b border-stroke px-5 py-4 dark:border-strokedark">
          <div>
            <h2
              id="order-merge-title"
              className="font-semibold text-black dark:text-white"
            >
              Fusionar órdenes
            </h2>
            <p className="mt-1 text-xs text-bodydark2">
              La orden de <b>arriba</b> recibe los productos; la de <b>abajo</b>{' '}
              se cierra y su mesa queda libre.
            </p>
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-2 text-bodydark2 transition hover:bg-gray-2 dark:hover:bg-meta-4"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <OrderPicker
            title="Orden destino (recibe los productos)"
            hint="Se mantiene abierta en su mesa actual."
            rooms={rooms}
            namePrefix="mergeTop"
            options={topOptions}
            loading={loadingTop}
            selectedId={topId}
            roomId={topRoom}
            search={topSearch}
            onRoomChange={setTopRoom}
            onSearchChange={setTopSearch}
            onSelect={setTopId}
          />

          <div className="flex justify-center">
            <span className="rounded-full bg-primary/10 p-1.5 text-primary">
              <ArrowDown className="h-4 w-4" />
            </span>
          </div>

          <OrderPicker
            title="Orden a fusionar (se cierra)"
            hint="Sus productos pasan a la orden destino."
            rooms={rooms}
            namePrefix="mergeBottom"
            options={bottomOptions}
            loading={loadingBottom}
            selectedId={bottomId}
            roomId={bottomRoom}
            search={bottomSearch}
            onRoomChange={setBottomRoom}
            onSearchChange={setBottomSearch}
            onSelect={setBottomId}
          />

          <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm dark:bg-primary/10">
            <div className="flex justify-between text-bodydark2">
              <span>Destino: {topOrder ? orderLabel(topOrder) : '—'}</span>
              <span>{formatOrderMoney(topOrder?.total ?? 0)}</span>
            </div>
            <div className="flex justify-between text-bodydark2">
              <span>
                A fusionar: {bottomOrder ? orderLabel(bottomOrder) : '—'}
              </span>
              <span>{formatOrderMoney(bottomOrder?.total ?? 0)}</span>
            </div>
            <div className="mt-1.5 flex justify-between border-t border-primary/20 pt-1.5 text-base font-semibold text-black dark:text-white">
              <span>Total al fusionar</span>
              <span>{formatOrderMoney(combined)}</span>
            </div>
          </div>
        </div>

        <footer className="grid grid-cols-2 gap-3 border-t border-stroke p-4 dark:border-strokedark">
          <ActionButton
            label="Cancelar"
            variant="outline"
            size="md"
            fullWidth
            onClick={() => onOpenChange(false)}
          />
          <ActionButton
            label="Fusionar"
            icon={<Combine className="h-4 w-4" />}
            variant="primary"
            size="md"
            fullWidth
            loading={busy}
            disabled={!ready || busy}
            onClick={async () => {
              if (topId != null && bottomId != null) {
                await onConfirm(topId, bottomId);
              }
            }}
          />
        </footer>
      </section>
    </div>
  );
};

export default OrderMergeModal;
